import { createSystem, defaultConfig, defineRecipe } from "@chakra-ui/react"

// The sales app (../sales/src/theme.js) carries a copy of this file, so the two
// read as one product; the repos cannot import each other. Change both together.

// The landing's buttons (landing_v2/src/styles/base.css, .cta and .rail):
// square corners, a near-black border, Inter 900 in tracked capitals, and a
// hover that swaps colours rather than lifting or glowing. The landing's main
// CTA is black, which would vanish on this dark canvas, so a solid button here
// takes the colour the landing gives a *selected* rail item (magenta, black
// text) and its hover swaps to black with the palette colour as the text — the
// same swap the landing's ghost CTA does (yellow → black on yellow).
const LANDING_LABEL = {
  fontFamily: "label",
  fontWeight: "900",
  textTransform: "uppercase",
  letterSpacing: "0.08em",
}

const buttonRecipe = defineRecipe({
  base: {
    borderRadius: "0",
    transition: "background-color 0.15s ease, color 0.15s ease, border-color 0.15s ease",
  },
  variants: {
    variant: {
      solid: {
        ...LANDING_LABEL,
        // 3px on a CTA (`--linea`), as on the landing; small sizes drop to the
        // 2px the landing uses for its own small controls (see compoundVariants).
        border: "3px solid var(--mc-button-border)",
        _hover: {
          bg: "colorPalette.swapBg",
          color: "colorPalette.swapFg",
        },
        _active: {
          bg: "colorPalette.swapBg",
          color: "colorPalette.swapFg",
        },
      },
      // The landing's rail button: a dark fill, a light 2px border, and a
      // border that turns white on hover.
      outline: {
        ...LANDING_LABEL,
        borderWidth: "2px",
        borderColor: "var(--mc-outline-border)",
        bg: "var(--mc-outline-bg)",
        _hover: {
          borderColor: "var(--mc-outline-border-hover)",
          bg: "var(--mc-outline-bg)",
        },
        _active: {
          bg: "var(--mc-outline-bg-active)",
        },
      },
      ghost: {
        _hover: {
          bg: "var(--mc-ghost-hover)",
        },
        _active: {
          bg: "var(--mc-ghost-active)",
        },
      },
      subtle: {
        _hover: {
          filter: "brightness(1.15)",
        },
        _active: {
          filter: "brightness(0.85)",
        },
      },
    },
  },
  compoundVariants: [
    {
      variant: "solid",
      size: ["2xs", "xs", "sm"],
      css: { borderWidth: "2px", letterSpacing: "0.06em" },
    },
  ],
})

const iconButtonRecipe = defineRecipe({
  base: {
    borderRadius: "0",
    transition: "background-color 0.15s ease, transform 0.1s ease",
    _hover: {
      bg: "var(--mc-icon-hover)",
    },
    _active: {
      transform: "scale(0.94)",
    },
  },
})

// One semantic palette in the shape Chakra's components read.
// `lightSolid` / `lightContrast` are for the one palette whose dark-mode solid
// is a light colour (neutral), which would vanish on a white page. `swapBg` /
// `swapFg` are the solid button's hover swap (black, palette colour as text);
// neutral's light solid is already black, so its swap inverts to white instead.
const palette = ({ solid, contrast = '#171b20', fg = solid, subtle, muted, emphasized, light, lightSolid = solid, lightContrast = contrast, lightSwapBg = '#171b20', lightSwapFg = lightSolid }) => ({
  solid: { value: { _light: lightSolid, _dark: solid } },
  contrast: { value: { _light: lightContrast, _dark: contrast } },
  swapBg: { value: { _light: lightSwapBg, _dark: '#171b20' } },
  swapFg: { value: { _light: lightSwapFg, _dark: solid } },
  fg: { value: { _light: `{colors.${light}.700}`, _dark: fg } },
  subtle: { value: { _light: `{colors.${light}.50}`, _dark: subtle } },
  muted: { value: { _light: `{colors.${light}.100}`, _dark: muted } },
  emphasized: { value: { _light: `{colors.${light}.200}`, _dark: emphasized } },
  // Focus is azul everywhere, whatever the control's own colour.
  focusRing: { value: '#0c8ce9' },
})

export const system = createSystem(defaultConfig, {
  // The default palette is neutral, so a component nobody gave a colour —
  // a plain button, a switch, a checkbox, a tab, a progress bar — is quiet.
  // Magenta has to be asked for (`colorPalette="brand"`), and only the
  // screen's primary action asks.
  globalCss: {
    html: {
      colorPalette: "neutral",
    },
  },
  theme: {
    tokens: {
      fonts: {
        heading: { value: `'DM Sans', sans-serif` },
        body: { value: `'DM Sans', sans-serif` },
        mono: { value: `'JetBrains Mono', monospace` },
        // The landing's button face (landing_v2 loads Inter 900).
        label: { value: `'Inter', 'Helvetica Neue', Arial, sans-serif` },
      },
      colors: {
        // The landing's magenta (--magenta #FE6ABE at 400, --rosa #FE88D4 at 300).
        brand: {
          50: { value: '#fff0f8' },
          100: { value: '#ffd9ee' },
          200: { value: '#ffb8e0' },
          300: { value: '#fe88d4' },
          400: { value: '#fe6abe' },
          500: { value: '#f04aa9' },
          600: { value: '#d5318f' },
          700: { value: '#ad2373' },
          800: { value: '#821a57' },
          900: { value: '#59123c' },
          950: { value: '#380b26' },
        },
      },
    },
    semanticTokens: {
      colors: {
        // The primary action. See the role table at the top of index.css.
        brand: {
          solid: { value: '{colors.brand.400}' },
          contrast: { value: '#171b20' },
          swapBg: { value: '#171b20' },
          swapFg: { value: '{colors.brand.400}' },
          fg: { value: { _light: '{colors.brand.700}', _dark: '{colors.brand.300}' } },
          muted: { value: { _light: '{colors.brand.100}', _dark: '{colors.brand.900}' } },
          subtle: { value: { _light: '{colors.brand.50}', _dark: '{colors.brand.950}' } },
          emphasized: { value: { _light: '{colors.brand.200}', _dark: '{colors.brand.800}' } },
          focusRing: { value: '#0c8ce9' },
        },
        // The default. A checked switch or a selected chip is light-on-dark,
        // not a colour: selection is not an action.
        neutral: palette({ solid: '#e6e8eb', subtle: '#252b33', muted: '#2e353e', emphasized: '#3a424c', light: 'gray', lightSolid: '#171b20', lightContrast: '#ffffff', lightSwapBg: '#ffffff', lightSwapFg: '#171b20' }),
        // Chakra's status palettes, remapped onto the brand's colours so every
        // existing colorPalette="red" / "green" / … follows without an edit.
        // Never the only signal: a status also carries a word or an icon.
        green: palette({ solid: '#4edfb5', subtle: '#15302a', muted: '#1b3f36', emphasized: '#245245', light: 'green' }),
        yellow: palette({ solid: '#ffec57', subtle: '#33301a', muted: '#45401f', emphasized: '#5a5326', light: 'yellow' }),
        red: palette({ solid: '#ff654a', subtle: '#3a1f1b', muted: '#4d2620', emphasized: '#66302a', light: 'red' }),
        // Azul as text is just under AA on a card, so its `fg` is a step lighter.
        blue: palette({ solid: '#0c8ce9', fg: '#4aa9ee', subtle: '#132a3d', muted: '#173651', emphasized: '#1d4669', light: 'blue' }),
        // Chakra's dark surfaces, moved onto the landing's near-black: the page
        // is #171B20 and a card one step up is #1E232A.
        bg: {
          DEFAULT: { value: { _light: '{colors.white}', _dark: '#171b20' } },
          subtle: { value: { _light: '{colors.gray.50}', _dark: '#1b2026' } },
          muted: { value: { _light: '{colors.gray.100}', _dark: '#252b33' } },
          emphasized: { value: { _light: '{colors.gray.200}', _dark: '#2e353e' } },
          panel: { value: { _light: '{colors.white}', _dark: '#1e232a' } },
        },
        fg: {
          DEFAULT: { value: { _light: '{colors.black}', _dark: '#e6e8eb' } },
          muted: { value: { _light: '{colors.gray.600}', _dark: '#a3a8af' } },
          subtle: { value: { _light: '{colors.gray.500}', _dark: '#8a9098' } },
        },
        border: {
          DEFAULT: { value: { _light: '{colors.gray.200}', _dark: 'rgba(230, 232, 235, 0.14)' } },
          muted: { value: { _light: '{colors.gray.100}', _dark: 'rgba(230, 232, 235, 0.10)' } },
          subtle: { value: { _light: '{colors.gray.50}', _dark: 'rgba(230, 232, 235, 0.07)' } },
          emphasized: { value: { _light: '{colors.gray.300}', _dark: 'rgba(230, 232, 235, 0.24)' } },
        },
      },
    },
    recipes: {
      button: buttonRecipe,
      iconButton: iconButtonRecipe,
    },
  },
})
